/**
 * Wrapper around Material UI button that allows button to be put into a loading
 * state, which shows a loading spinner over the disabled button
 */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Fab from '@material-ui/core/Fab';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/styles';

const propTypes = {
  btnClassName: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.shape({
    wrapper: PropTypes.string.isRequired,
    progress: PropTypes.string.isRequired,
    miniFabProgress: PropTypes.string.isRequired,
    fabProgress: PropTypes.string.isRequired,
    nonFabProgress: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  mini: PropTypes.bool,
  tooltip: PropTypes.string,
  tooltipPlacement: PropTypes.string,
};

const defaultProps = {
  btnClassName: null,
  className: null,
  loading: false,
  disabled: false,
  variant: null,
  mini: false,
  tooltip: '',
  tooltipPlacement: 'bottom',
};

export function UnstyledBusyButton({
  btnClassName,
  className,
  classes,
  loading,
  disabled,
  variant,
  mini,
  tooltip,
  tooltipPlacement,
  ...rest
}) {
  let progressSize = 25;
  let thickness = 3.2;
  const progressClasses = [classes.progress];

  if (loading) {
    if (variant === 'fab') {
      if (mini) {
        progressSize = 47;
        thickness = 4.5;
        progressClasses.push(classes.miniFabProgress);
      } else {
        progressSize = 65;
        thickness = 4;
        progressClasses.push(classes.fabProgress);
      }
    } else {
      progressClasses.push(classes.nonFabProgress);
    }
  }

  let button;

  if (variant === 'fab' && tooltip) {
    button = (
      <Tooltip title={tooltip} placement={tooltipPlacement}>
        <span>
          <Fab disabled={disabled || loading} {...rest} className={btnClassName} />
        </span>
      </Tooltip>
    );
  } else if (variant === 'fab') {
    button = <Fab disabled={disabled || loading} {...rest} className={btnClassName} />;
  } else if (tooltip) {
    button = (
      <Tooltip title={tooltip} placement={tooltipPlacement}>
        <span>
          <Button
            variant={variant}
            disabled={disabled || loading}
            {...rest}
            className={btnClassName}
          />
        </span>
      </Tooltip>
    );
  } else {
    button = (
      <Button variant={variant} disabled={disabled || loading} {...rest} className={btnClassName} />
    );
  }

  return (
    <div className={className}>
      <div className={classes.wrapper}>
        {button}
        {loading && (
          <CircularProgress
            size={progressSize}
            className={classnames(progressClasses)}
            thickness={thickness}
          />
        )}
      </div>
    </div>
  );
}

UnstyledBusyButton.propTypes = propTypes;
UnstyledBusyButton.defaultProps = defaultProps;

const styles = () => ({
  wrapper: {
    position: 'relative',
  },
  progress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
  },
  miniFabProgress: {
    marginTop: -23,
    marginLeft: -22,
  },
  fabProgress: {
    marginTop: -32,
    marginLeft: -31,
  },
  nonFabProgress: {
    marginTop: -12,
    marginLeft: -12,
  },
});

export default withStyles(styles)(UnstyledBusyButton);
